import type { ComponentType } from 'react';
import {
  replaceContentScanners,
  type ContentScannerComponentProps,
  type ContentScannerDescriptor,
  type ContentScannerPlacement,
  type ContentScannerTarget,
} from '@reflex/ui/client/content-scanners';
import { getPluginComponent, type PluginManifest } from './plugin-registry';

/**
 * Wrap a resolved plugin slot component with a loading fallback so it can be
 * dropped straight into the markdown render tree. Plugin slot components
 * render `null` until their module resolves (see `createSlotComponent`), so
 * the fallback rides in via the reserved `slotFallback` prop and preserves
 * the inline reading experience while the chunk loads — or if it ever fails:
 *  - `text` scanners fall back to the matched text (e.g. the raw file path).
 *  - inline `url` scanners fall back to a plain link carrying the label.
 *  - `trailing` scanners fall back to nothing: the link is already rendered by
 *    the surface, and a placeholder after the prose would restore the bottom
 *    margin markdown blocks zero with `last:mb-0`.
 */
function wrapScannerComponent(
  Scanner: ComponentType,
  target: ContentScannerTarget,
  placement: ContentScannerPlacement,
): ComponentType<ContentScannerComponentProps> {
  const SlotScanner = Scanner as ComponentType<
    ContentScannerComponentProps & { slotFallback?: React.ReactNode }
  >;
  return function ScannerBoundary(props: ContentScannerComponentProps) {
    let fallback: React.ReactNode = <>{props.match}</>;
    if (placement === 'trailing') {
      fallback = null;
    } else if (target === 'url') {
      fallback = (
        <a href={props.match} target="_blank" rel="noopener noreferrer">
          {props.children ?? props.match}
        </a>
      );
    }
    return <SlotScanner {...props} slotFallback={fallback} />;
  };
}

/** Ids registered by the previous `resolveContentScanners` pass. */
let registeredManifestIds = new Set<string>();
/**
 * The manifests array the last pass ran against. `usePlugins` calls this from
 * an effect that fires in every component using the hook, but React Query
 * hands back a stable reference for unchanged data — so we skip redundant
 * reconciliations (and the subscriber re-renders they trigger) by reference.
 */
let lastManifests: PluginManifest[] | null = null;

/**
 * Resolve every enabled plugin manifest's `contentScanners` into runtime
 * descriptors and register them into the shared `@reflex/ui` registry that
 * backs `MarkdownContent`. Modeled on `resolveFlowTriggerRenderers`: compile
 * the regex (guarded), resolve the lazy component by `componentPath`, and skip
 * entries that don't resolve (the wiring-issue collector reports those).
 *
 * Reconciles add/remove across manifest refreshes by dropping any previously
 * registered manifest id that is no longer present, so a disabled/uninstalled
 * plugin's scanner stops firing without leaking.
 */
export function resolveContentScanners(manifests: PluginManifest[]): void {
  if (manifests === lastManifests) return;
  lastManifests = manifests;
  const descriptors: ContentScannerDescriptor[] = [];
  const seen = new Set<string>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const def of manifest.web?.contentScanners ?? []) {
      if (seen.has(def.id)) {
        // First registration wins per id; later duplicates are ignored.
        continue;
      }
      let regex: RegExp;
      try {
        regex = new RegExp(def.pattern, def.flags);
      } catch {
        // Invalid pattern — skip. `collectPluginWebWiringIssues` doesn't cover
        // regex validity, so surface it in dev to aid the plugin author.
        if (import.meta.env?.MODE !== 'test') {
          console.warn(
            `[content-scanners] plugin "${manifest.name}" scanner "${def.id}" has an invalid pattern: ${def.pattern}`,
          );
        }
        continue;
      }
      const component = getPluginComponent(def.componentPath);
      if (!component) continue;
      seen.add(def.id);
      const placement: ContentScannerPlacement =
        def.target === 'url' && def.placement === 'trailing' ? 'trailing' : 'inline';
      descriptors.push({
        id: def.id,
        target: def.target,
        regex,
        order: def.order,
        placement,
        Component: wrapScannerComponent(component, def.target, placement),
      });
    }
  }
  const nextIds = new Set(descriptors.map((d) => d.id));
  const prevIds = registeredManifestIds;
  replaceContentScanners(descriptors, (id) => prevIds.has(id) || nextIds.has(id));
  registeredManifestIds = nextIds;
}
